import { AfterViewInit, Directive, ElementRef, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AvanoPopoverSupport, PopoverStateChange } from '../avano-popover/avano-popover.directive';
import { AvanoDomHandler } from '../common/domhandler';
import { TypesafeChanges } from '../utils/typesafety.utils';

export type AvanoButtonType = 'callToAction' | 'error';
@Directive({
  selector: '[avanoButton]',
  providers: [
    {
      provide: AvanoPopoverSupport,
      useExisting: forwardRef(() => AvanoButtonDirective)
    }
  ]
})
export class AvanoButtonDirective implements AfterViewInit, OnDestroy, OnChanges, AvanoPopoverSupport {
  @Input() icon: string;
  /** @deprecated use buttonType instead */
  @Input() callToAction: boolean;
  @Input() label: string;
  @Input() buttonType: AvanoButtonType;

  public initialized: boolean;
  public _label: string;
  public _initialStyleClass: string;
  constructor(public el: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges & TypesafeChanges<AvanoButtonDirective>): void {
    if (!this.initialized) return;
    this.typesafeOnChanges(changes as TypesafeChanges<AvanoButtonDirective>);
  }

  typesafeOnChanges(changes: TypesafeChanges<AvanoButtonDirective>) {
    if (changes.label) {
      AvanoDomHandler.findSingle(this.el.nativeElement, '.avano-button-label').textContent = changes.label.currentValue || '&nbsp;';
    }
    this.setStyleClass();
    if (changes.icon) {
      this.updateIcon();
    }
  }

  ngAfterViewInit() {
    this._initialStyleClass = this.el.nativeElement.className;
    AvanoDomHandler.addMultipleClasses(this.el.nativeElement, this.getStyleClass());

    this.updateIcon();

    const labelElement = document.createElement('span');
    if (this.icon && !this.label) {
      labelElement.setAttribute('aria-hidden', 'true');
    }
    labelElement.className = 'avano-button-label';

    if (this.label) {
      labelElement.appendChild(document.createTextNode(this.label));
    } else {
      labelElement.innerHTML = '&nbsp;';
    }
    this.el.nativeElement.appendChild(labelElement);
    this.initialized = true;
  }

  createIconEl(): HTMLElement {
    const iconElement = document.createElement('span');
    iconElement.className = 'avano-button-icon';
    iconElement.setAttribute('aria-hidden', 'true');

    this.el.nativeElement.insertBefore(iconElement, this.el.nativeElement.firstChild)
    return iconElement;
  }

  updateIcon() {
    const existingIconElement: Element = AvanoDomHandler.findSingle(this.el.nativeElement, '.avano-button-icon');
    if (!this.icon)  {
      existingIconElement?.remove();
      return;
    }
    const iconElement = existingIconElement ?? this.createIconEl();
    iconElement.className = 'avano-button-icon ' + this.icon;
  }

  setStyleClass() {
    const styleClass = this.getStyleClass();
    this.el.nativeElement.className = styleClass + ' ' + this._initialStyleClass;
  }

  getStyleClass(): string {
    const styleClasses = ['avano-button'];
    if (this.icon && !this.label) {
      styleClasses.push('avano-button-icon-only');
    }
    switch (this.buttonType) {
      case 'callToAction':
        styleClasses.push('avano-button-call-to-action');
        break;
      case 'error':
        styleClasses.push('avano-button-error');
        break;
      default:
        if (this.callToAction === true) styleClasses.push('avano-button-call-to-action');
        break;
    }
    return styleClasses.join(' ');
  }

  public popoverStateChanged(popoverState: PopoverStateChange) {
    if (popoverState.enabled) {
      this.addPopoverIndicator();
    }
  }

  ngOnDestroy() {
    this.initialized = false;
  }

  private addPopoverIndicator() {
    const popoverIndicatorClass = 'avano-button-popover-indicator';
    const hasIndicator = this.el.nativeElement.querySelector(`.${popoverIndicatorClass}`) !== null;
    if (hasIndicator) return;

    const iconElement = document.createElement('span');
    iconElement.setAttribute('aria-hidden', 'true');
    iconElement.className = `${popoverIndicatorClass} icon-content-expand`;

    this.el.nativeElement.appendChild(iconElement);
  }
}
